import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import { ConfirmationService, MessageService, MenuItem, TreeNode } from 'primeng/api';
import { DataService } from 'src/app/services/data.service';
import { ExportService } from 'src/app/services/export.service';
import { GlobalConstants } from 'src/app/services/global-constants';
import { OrgUnitsService } from 'src/app/services/org-units.service';
import { PermissionHandlerService } from 'src/app/services/permission-handler.service';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { TranslatorPipe } from 'src/app/services/translator.pipe';
import { ContentReportExpertsService } from 'src/app/services/content-report-experts.service';

interface OUData {
  ou: any;
  contentUpdates: any[];
  managers: any[]; // Assigned report experts for this OU (Content Experts Management)
  selectedManager?: any; // Track selected manager for each OU
  managersError?: boolean; // Assigned experts could not be loaded
}

@Component({
  selector: 'app-create-periodic-reports',
  templateUrl: './create-periodic-reports.component.html',
  styleUrl: '../preodic-reports.component.scss'
})
export class CreatePeriodicReportsComponent implements OnInit {
  
  treeData: any[] = [];
  GlobalConstants = GlobalConstants;
  staticURL = GlobalConstants.staticFilesURL;
  periodicReportForm: FormGroup;
  isManagerMode = false;
  @Input() mode: string = 'create';
  managers: any[] = [];
  selectedTreeData : any = [];
  selectedTreeTags_current: any;
  selectedTreeData_orginal: any;
  types: any[] = [];
  selectedTreeTags: MenuItem[] = [];
  currentDate = new Date();
  enhancedDate!: Date;
  contentUpdateData: any[] = [];
  @Input() report: any;
  isCreate: boolean = false;
  loading: boolean = false;
  uid: string = '';
  @Output() closeModal: EventEmitter<boolean> = new EventEmitter<boolean>();
  
  ouDataMap: Map<string, any[]> = new Map(); // Map to store data for each OU
  selectedOUsData: OUData[] = []; // Array to store processed data for each OU
  maxDate: Date = new Date(); // Today's date as max date
  startDate: Date | null = null; // Optional start date
  endDate: Date | null = null; // Optional end date
  
  constructor(
    private dataService: DataService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private orgUnits: OrgUnitsService,
    private permissionService: PermissionHandlerService,
    private exportService: ExportService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private contentReportExperts: ContentReportExpertsService
  ) {
    this.periodicReportForm = this.fb.group({
      ou: [[], Validators.required],
      manager: [''],
      days: ['', Validators.required],
      time: ['', Validators.required],
      startDate: [null], // Add startDate control
      endDate: [null], // Add endDate control
      confirmation: [{value: false, disabled: !this.isManagerMode}],
      comment: [{value: '', disabled: !this.isManagerMode}],
      agreement: [{value: false, disabled: !this.isManagerMode}],
      status: ['PENDING']
    });
  }


  ngOnInit() {
    this.loading = true;
    this.uid = localStorage.getItem('uid') || '';
    Promise.all([
      this.getTreeData(),
    ]).then(e => {
      if (this.mode === 'Manager') {
        this.isManagerMode = true;
        this.report.status !== 'SIGNED' ? this.enableManagerControls() : this.disableManagerControls();
      } else if (this.mode === 'create') {
        this.isCreate = true;
        this.isManagerMode = false;
        this.disableManagerControls();
        // Enable OU selection only in create mode
        this.periodicReportForm.get('ou')?.enable();
      } else {
        this.isManagerMode = false;
        if (this.report?.status !== 'SIGNED' && this.checkAdminManagerMode()) {
          this.mode = 'Manager';
          this.enableManagerControls();
        } else {
          this.disableManagerControls();
        }
        // Disable OU selection in non-create modes
        this.periodicReportForm.get('ou')?.disable();
      }

      if (this.report?.status === 'SIGNED') {
        this.disableAllControls();
      }

      if (this.mode !== 'create') {
        this.setReportData();
      }

      this.periodicReportForm.get('days')?.valueChanges.subscribe(days => {
        if(days < 0){
          this.periodicReportForm.get('days')?.setValue(0);
        };
        this.calculateDateTime();
      });

      this.periodicReportForm.get('time')?.valueChanges.subscribe((value: Date) => {
        if (value) {
          const newTime = new Date(value);
          newTime.setMinutes(0); // Always set minutes to 00
    
          // Update the form value without triggering an infinite loop
          if (newTime.getTime() !== value.getTime()) {
            this.periodicReportForm.get('time')?.setValue(newTime, { emitEvent: false });
          }
        }
      });

      // Add endDate change subscription
      this.periodicReportForm.get('endDate')?.valueChanges.subscribe((value: Date) => {
        if (value) {
          // Ensure end date is not greater than today
          if (value > this.maxDate) {
            this.periodicReportForm.get('endDate')?.setValue(this.maxDate);
          }
          // Update currentDate for report generation
          this.currentDate = value;
        } else {
          // Reset to today if end date is cleared
          this.currentDate = new Date();
        }
        this.calculateDateTime();
      });

      // Add startDate change subscription
      this.periodicReportForm.get('startDate')?.valueChanges.subscribe((value: Date) => {
        if (value) {
          // Ensure start date is not greater than today (calendar already enforces this via maxDate)
          if (value > this.maxDate) {
            this.periodicReportForm.get('startDate')?.setValue(this.maxDate, { emitEvent: false });
            return;
          }
          this.startDate = value;
          const endDate = this.periodicReportForm.get('endDate')?.value;
          if (endDate && value && endDate < value) {
            // Keep endDate >= startDate
            this.periodicReportForm.get('endDate')?.setValue(value, { emitEvent: false });
          }
          this.calculateDateTime();
        } else {
          this.startDate = null;
        }
      });


      this.mode
      
      this.loading = false;
    });

  }


   checkAdminManagerMode(){
   let allowedOus = this.flattenTree(this.selectedTreeData)
   const ouId = this.report?.ou?._id;
   // An expert assigned to this OU signs it, whatever their role permissions.
   if (this.contentReportExperts.getCachedOuIds().includes(ouId)) {
     return true;
   }
   return allowedOus.includes(ouId) && this.hasPermission('CONTENT_APPROVE','a')

   }


   flattenTree(root: TreeNode[]): any[] {
    const result: any[] = [];
    root?.map(e => this.flattenNode(e, result))
    // this.flattenNode(root, result);
    return result;
  }

  private flattenNode(node: TreeNode, result: any[]): void {
    result.push((node as any)?._id);
    if(node?.children){
      for (const child of node.children) {
        this.flattenNode(child, result);
      }
    }
  }

  disableAllControls() {
    this.periodicReportForm.get('ou')?.disable();
    this.periodicReportForm.get('manager')?.disable();
    this.periodicReportForm.get('days')?.disable();
    this.periodicReportForm.get('time')?.disable();
    this.disableManagerControls();
  }

  hasPermission(key:string, flag:string) {
    return this.permissionService.hasPermission(key, flag);
  }

  async setReportData() {
    if (!this.report) {
      return;
    }

    // Handle OU data based on mode
    if (this.mode === 'create') {
      // For create mode, we'll use the tree selection
      this.periodicReportForm.patchValue({
        ou: [], // Initialize as empty array for multiple selection
        manager: '',
        days: '',
        time: '',
        status: 'PENDING'
      });
      // Enable OU selection in create mode
      this.periodicReportForm.get('ou')?.enable();
    } else {
      // For edit/view modes, set the OU data
      const ouNode = this.findNodeById(this.treeData, this.report.ou._id);
      if (ouNode) {
        // For single OU in edit/view mode
        this.periodicReportForm.patchValue({
          ou: [ouNode],
          manager: this.report?.manager,
          days: this.report.days,
          time: this.report.time,
          status: this.report.status,
          comment: this.translate(this.report?.comment),
          agreement: this.report?.agreement,
          confirmation: this.report?.confirmation,
          startDate: this.report.startDate ? new Date(this.report.startDate) : null,
          endDate: this.report.endDate ? new Date(this.report.endDate) : null
        });
        // Disable OU selection in non-create modes
        this.periodicReportForm.get('ou')?.disable();

        // Set up the OU data map for the existing report
        if (this.report.states) {
          this.ouDataMap.set(this.report.ou._id, this.report.states);
          this.selectedOUsData = [{
            ou: ouNode,
            contentUpdates: this.report.states,
            managers: [],
            selectedManager: this.report?.manager || undefined
          }];
        }
      } else {
        // Fallback: patch with raw report.ou and log a warning
        console.warn('OU node not found in treeData, patching with raw report.ou');
        this.periodicReportForm.patchValue({
          ou: [this.report.ou],
          manager: this.report?.manager,
          days: this.report.days,
          time: this.report.time,
          status: this.report.status,
          comment: this.report?.comment,
          agreement: this.report?.agreement,
          confirmation: this.report?.confirmation,
          startDate: this.report.startDate ? new Date(this.report.startDate) : null,
          endDate: this.report.endDate ? new Date(this.report.endDate) : null
        });
        this.periodicReportForm.get('ou')?.disable();
        if (this.report.states) {
          this.ouDataMap.set(this.report.ou._id, this.report.states);
          this.selectedOUsData = [{
            ou: this.report.ou,
            contentUpdates: this.report.states,
            managers: [],
            selectedManager: this.report?.manager || undefined
          }];
        }
      }
    }

    // Set up dates and time
    this.enhancedDate = new Date(this.report.sendingDate);
    if (this.report.time) {
      const timeObj = this.convertTimeStringToDate(this.report.time);
      this.periodicReportForm.get('time')?.setValue(timeObj);
    }

    // Only fetch content updates if there are no states in the report
    if (this.mode !== 'create' && this.report.status !== 'SIGNED' && !this.report.states) {
      await this.getContentUpdates();
    }

    this.cdr.detectChanges();
  }

  convertTimeStringToDate(timeString: string): Date {
    const [time, period] = timeString.split(' ');
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(period === 'PM' ? parseInt(hours) + 12 : parseInt(hours));
    date.setMinutes(parseInt(minutes)); 
    return date;
  }
  
  findNodeById(data: any[], nodeId: string): any | undefined {
    if (!Array.isArray(data)) return undefined;
    for (const node of data) {
      if (!node) continue; // Defensive: skip undefined nodes
      if (node && node._id === nodeId) {
        return node;
      }
      if (node && node.children) {
        const result = this.findNodeById(node.children, nodeId);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  calculateDateTime() {

    const now = new Date();

    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() + this.periodicReportForm.get('days')?.value);
    
    // Get the day of the week (0 = Sunday, 5 = Friday, 6 = Saturday)
    const dayOfWeek = startDate.getDay();
    
    // If the date falls on Friday (5) or Saturday (6), adjust to Sunday (0)
    if (dayOfWeek === 5) { // Friday
      startDate.setDate(startDate.getDate() + 2); // Add 2 days to reach Sunday
    } else if (dayOfWeek === 6) { // Saturday
      startDate.setDate(startDate.getDate() + 1); // Add 1 day to reach Sunday
    }
    
    this.enhancedDate = startDate;
  }
  get status(): string {
    return this.periodicReportForm.get('status')?.value || 'PENDING';
  }

  async getContentUpdates() {
    try {
      if (!!this.report && this.report?.status === 'SIGNED') {
        this.contentUpdateData = this.report.states;
        
        return;
      }

      const selectedOUs = this.periodicReportForm.get('ou')?.value || [];
      if (!Array.isArray(selectedOUs)) {
        return;
      }

      // Don't clear data if we're in edit/view mode and have existing data
      if (this.mode === 'create') {
        this.selectedOUsData = [];
        this.ouDataMap.clear();
      }

      // Build createdAt filter from start/end dates if set
      const startDate = this.periodicReportForm.get('startDate')?.value as Date | null;
      const endDate = this.periodicReportForm.get('endDate')?.value as Date | null;
      debugger
      let createdAtFilter: any = undefined;
      if (startDate || endDate) {
        createdAtFilter = {} as any;
        if (startDate) {
          createdAtFilter.$gte = new Date(startDate).toISOString();
        }
        if (endDate) {
          createdAtFilter.$lte = new Date(endDate).toISOString();
        }
      }

      // Assigned report experts for every selected OU, in one request.
      const ouIdsToLoad = selectedOUs
        .filter((ou: any) => !(this.mode !== 'create' && this.ouDataMap.has(ou._id)))
        .map((ou: any) => ou._id);
      const expertsByOu = this.mode === 'create'
        ? await this.contentReportExperts.getAssignedForReports(ouIdsToLoad)
        : {};

      // Process each selected OU
      for (const ou of selectedOUs) {
        // Skip if we already have data for this OU in edit/view mode
        if (this.mode !== 'create' && this.ouDataMap.has(ou._id)) {
          continue;
        }

        const ousArray = this.flattenEntityTree(ou);
        
        // Make API calls for each type for this OU
        const types = ['OU CHANGE', 'EDIT', 'ADD SERVICE', 'DELETE'];
        const responses = await Promise.all(types.map(type => 
          this.dataService.postData('content-update/get?offset=2&page=1', {
            type: type,
            ous: ousArray,
            status: 'APPROVED',
            createdAt: createdAtFilter // Add createdAt filter
          })
        ));

        // Process responses for this OU
        let finalData: any[] = [];
        responses.forEach((res, index) => {
          if (!res.error && res.data[0]) {
            const type = types[index];
            if (res.data[0].stats_type) {
              const typeStat = res.data[0].data_type;
              if (typeStat) {
                finalData.push({
                  data: typeStat,
                  type: type
                });
              }
            }
          }
        });

        // Experts assigned to this OU on the Content Experts Management page
        const managers: any[] = expertsByOu?.[ou._id] || [];

        // Store data for this OU
        this.ouDataMap.set(ou._id, finalData);
        
        // Only add to selectedOUsData if not already present
        if (!this.selectedOUsData.some(data => data.ou._id === ou._id)) {
          this.selectedOUsData.push({
            ou: ou,
            contentUpdates: finalData,
            managers: managers,
            selectedManager: managers.length === 1 ? managers[0] : undefined,
            managersError: expertsByOu === null
          });
        }
      }

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error in getContentUpdates:', error);
      // this.messageService.add({
      //   severity: 'error',
      //   summary: this.translate('Error'),
      //   detail: this.translate('Failed to fetch content updates')
      // });
    }
  }


  

  // Helper method to flatten entity tree
  private flattenEntityTree(entity: any): string[] {
    let ousArray: string[] = [];
    
    if (entity._id) {
      ousArray.push(entity._id);
    }

    if (entity.children && Array.isArray(entity.children)) {
      entity.children.forEach((child: any) => {
        ousArray = ousArray.concat(this.flattenEntityTree(child));
      });
    }

    return ousArray;
  }


  enableManagerControls() {
    this.periodicReportForm.get('confirmation')?.enable();
    this.periodicReportForm.get('comment')?.enable();
    this.periodicReportForm.get('agreement')?.enable();
  }

  disableManagerControls() {
    this.periodicReportForm.get('confirmation')?.disable();
    this.periodicReportForm.get('comment')?.disable();
    this.periodicReportForm.get('agreement')?.disable();
  }

  translate(key: string) {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  async getManagers(id?: any) {
    try {

      const entityValue = this.periodicReportForm.get('ou')?.value ? this.periodicReportForm.get('ou')?.value : id;
  
      let res = await this.dataService.getData('ou/manager?id='+ entityValue._id);
      if (!res.error) {
        this.managers = res.data[0].managers.map((manager: any) => {
          return {
            _id: manager._id,
            name: manager.name.first + ' ' + manager.name.middle + ' ' + manager.name.last,
            email: manager.email
          };
        });
        this.periodicReportForm.get('manager')?.reset();
      }
     else {
        this.messageService.add({
          severity: 'error', 
          summary: this.translate('Error'),
          detail: this.translate('No managers found for this entity')
        });
        this.managers = [];
      }

      return 
    } catch (error) {
      console.log('Error fetching managers:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to load managers')
      });
    }
  }

  

  /** True while a submit is in flight, so a double click cannot send the report twice. */
  submitting = false;

  async submitForm() {
    if (this.submitting) {
      return;
    }
    this.submitting = true;
    try {
      if (this.mode === 'Manager' && this.report.status !== 'SIGNED') {
        await this.updateManager();
        return;
      }

      if (this.isCreate) {
        await this.createReport();
        return;
      }

      if (this.mode === 'Admin' && this.report.status !== 'SIGNED') {
        await this.updateAdmin();
        return;
      }

      this.closeModal.emit(true);
    } finally {
      this.submitting = false;
    }
  }

  private checkOUPermission(reportOUId: string): boolean {

    try {
      // Get allowed OUs from the tree data
      const allowedOUs = JSON.parse(localStorage.getItem('ou') || '[]') || []
      
      // The user's own OUs, or an OU they are assigned to as report expert
      const hasPermission = allowedOUs.some((ou: any) => ou && ou._id === reportOUId)
        || this.contentReportExperts.getCachedOuIds().includes(reportOUId);
      
      if (!hasPermission) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('You are not authorized to update reports from this organizational unit')
        });
      }
      
      return hasPermission;
    } catch (error) {
      console.error('Error checking OU permission:', error);
      return false;
    }
  }

  async updateAdmin(){
    try {
      const formData = this.periodicReportForm.value;
      if(!this.validateForm(formData)){
        return;
      }

      let data : any = {
        ou: formData.ou._id,
        days: formData.days,
        time: new Date(formData.time).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }),
        sendingDate: this.enhancedDate.toISOString(),
        status: 'PENDING',
        reportForm: formData.startDate ? new Date(formData.startDate).toISOString() : this.report.reportForm,
        reportTo: formData.endDate ? new Date(formData.endDate).toISOString() : this.currentDate.toISOString(),
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        states: this.contentUpdateData
      }

      if (this.uid === this.report.manager._id && this.report.status !== 'SIGNED') {
        data.confirmation = formData.confirmation
        data.comment = formData.comment
        data.agreement = formData.agreement
        data.status = 'SIGNED'
      }

      const response = await this.dataService.postData('content-update-reports/'+this.report._id, data,'PUT');
      if(!response.error){
        this.messageService.add({
          severity: 'success', 
          summary: this.translate('Success'),
          detail: this.translate('Report updated successfully')
        });
        this.closeModal.emit(true);
      }else{
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'), 
          detail: this.translate('Failed to update report')
        });
      }
    } catch (error) {
      console.error('Error in updateAdmin:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update report')
      });
    }
  }

  async updateManager(){
    try {
      // Check OU permission before proceeding
      if (!this.checkOUPermission(this.report.ou._id)) {
        return;
      }

      const formData = this.periodicReportForm.value;
      if(!this.validateForm(formData)){
        return;
      }

      let data = { 
       confirmation: formData.confirmation,
       comment: formData.comment,
       agreement: formData.agreement,
       status: 'SIGNED',
       manager: {
        _id : localStorage.getItem('uid') || '',
        name: localStorage.getItem('username') || '',
        email: localStorage.getItem('email') || ''
       }
      }
      const response = await this.dataService.postData('content-update-reports/'+this.report._id, data,'PUT');
      if(!response.error){
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Manager updated successfully')
        });
        this.closeModal.emit(true);
      }else{
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate(response?.msg || 'Failed to update manager')
        });
      }
    } catch (error) {
      console.error('Error in updateManager:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update manager')  
      });
    }
  } 


  async createReport() {
    try {
      const formData = this.periodicReportForm.value;
      if(!this.validateForm(formData)){
        return;
      }

      const selectedOUs = formData.ou || [];
      if (!Array.isArray(selectedOUs) || selectedOUs.length === 0) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Select the ministry')
        });
        return;
      }

      // Every report is emailed to its unit's chosen expert, so each selected
      // unit needs one: none assigned yet, or several assigned and none picked.
      const unitName = (ou: any) => ou?.label || ou?.name || '';
      const withoutExperts = selectedOUs.filter((ou: any) => {
        const data = this.selectedOUsData.find(d => d.ou._id === ou._id);
        return !data || data.managers.length === 0;
      });
      if (withoutExperts.length > 0) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('No report experts are assigned to') + ': ' + withoutExperts.map(unitName).join('، ')
            + '. ' + this.translate('Assign one in Content Experts Management first.'),
          life: 8000
        });
        return;
      }
      const withoutChoice = selectedOUs.filter((ou: any) => {
        const data = this.selectedOUsData.find(d => d.ou._id === ou._id);
        return !data?.selectedManager;
      });
      if (withoutChoice.length > 0) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Choose the expert who receives the report for') + ': ' + withoutChoice.map(unitName).join('، '),
          life: 8000
        });
        return;
      }

      // Create base data for all reports
      const baseData = {
        days: formData.days,
        time: new Date(formData.time).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }),
        status: formData.status,
        sendingDate: this.enhancedDate.toISOString(),
        reportForm: formData.startDate ? new Date(formData.startDate).toISOString() : 'New Report form started',
        reportTo: formData.endDate ? new Date(formData.endDate).toISOString() : this.currentDate.toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined
      };

      this.ouDataMap

      // Create reports for each selected OU
      const createPromises = selectedOUs.map(async (ou: any) => {
        const ouData = this.selectedOUsData.find(data => data.ou._id === ou._id);
        
        const reportData: any = {
          ...baseData,
          ou: ou._id,
          states: ouData?.contentUpdates || []
        };
        // Attach manager if selected
        if (ouData && ouData.selectedManager) {
          reportData.manager = {
            _id: ouData.selectedManager._id,
            name: ouData.selectedManager.name,
            email: ouData.selectedManager.email
          };
        }
        return this.dataService.postData('content-update-reports/', reportData);
      });

      const responses = await Promise.all(createPromises);
      
      // Check results
      const allSuccessful = responses.every(response => !response.error);
      if (allSuccessful) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate('Success'),
          detail: this.translate('Reports created successfully')
        });
        this.closeModal.emit(true);
      } else {
        const failed = responses.filter(response => response.error);
        const failedCount = failed.length;
        this.messageService.add({
          severity: 'warn',
          summary: this.translate('Partial Success'),
          detail: this.translate(`Created ${responses.length - failedCount} reports, failed to create ${failedCount} reports`)
            + (failed[0]?.msg ? ' — ' + this.translate(failed[0].msg) : '')
        });
      }
    } catch (error) {
      console.error('Error in createReport:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to create reports')
      });
    }
  }
  

  validateForm(formData: any) {
    if(this.isCreate){
      if(!formData.ou || !Array.isArray(formData.ou) || formData.ou.length === 0){
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Please select at least one organizational unit')
        });
        return false;
      }

      if(!formData.days){
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Please select a number of days')
        });
        return false;  
      }

      if(!formData.time){
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Please select a time')
        });
        return false;
      }
    }
    if(this.mode == 'Manager'){
      if(!formData.confirmation){
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Please confirm the report')
        });
        return false;
      }
      if(!formData.comment){
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Please add comments')
        });
        return false;
      }
      if(!formData.agreement){
        this.messageService.add({
          severity: 'error',
          summary: this.translate('Error'),
          detail: this.translate('Please agree to the terms and conditions')
        });
        return false;
      }
    }
    return true;  
  }


  checkisImg(arg0: string | undefined): any {
    if (arg0) {
      return /\.(jpg|jpeg|png|gif|svg)/.test(arg0);
    } else {
      return false;
    }
  }

  async getTreeData() {
    this.treeData = await this.permissionService.extractOUChildren('CONTENT_UPDATE_LOGS', 'r');
    this.selectedTreeData_orginal = cloneDeep(this.treeData)
    this.changeSelectedTag(null);
    console.log("===>test data d",this.selectedTreeData);
  }

  findNodeByName(data: any[], nodeName: any): any | undefined {
    for (const node of data) {
      if (node.children) {
        const result = this.findNodeByName(node.children, nodeName);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  }

  async changeSelectedTag(event: any) {
    console.log("change tag event", event);
    this.selectedTreeTags_current = {
      "label": "Ministries/Entities"
    };
    if (this.selectedTreeTags_current.label) {
      console.log("change tag event1", event,this.selectedTreeData);
      await this.filterTreeByTag("Ministries/Entities");
    } else {
      this.selectedTreeData = null;
    }
  }

  filterTreeByTag(tag: string) {
    this.selectedTreeData = cloneDeep(this.selectedTreeData_orginal);
    this.selectedTreeData = this.selectedTreeData
      .map((node: any) => this.filterNodes(node, tag))
      .filter((filteredNode: any) => filteredNode !== null);
    console.log(this.selectedTreeData);

    if (this.selectedTreeData.length == 0) {
      let cloneitems: any = []
      this.selectedTreeData_orginal.forEach((item: any) => {
        cloneitems.push({
          data: item?.data,
          id: item?.id,
          label: item?.label,
          parent: item?.parent,
          tag: item?.tag,
          _id: item?._id,
          children: []
        })
      });

      console.log("cloneitems.children", cloneitems);
      this.selectedTreeData = cloneDeep(cloneitems);
      console.log("cloneitems.children this.selectedTreeData", this.selectedTreeData);
    }
  }

  filterNodes(node: any, tag: string): any | null {
    if (!node) return null; // Defensive: skip undefined nodes
    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
      node.children = node.children
        .map((childNode: any) => this.filterNodes(childNode, tag))
        .filter((filteredChild: any) => filteredChild !== null);
    }

    if (node.tag === tag || (node.children && Array.isArray(node.children) && node.children.length > 0)) {
      return node;
    }
    return null;
  }

  // Helper method to get data for a specific OU
  getOUData(ouId: string): any[] {
    return this.ouDataMap.get(ouId) || [];
  }

  async onEndDateSelect(event: Date) {
    try {
      this.loading = true;
      // Update the current date for report generation
      this.currentDate = event;
      
      // Recalculate the enhanced date based on the new end date
      this.calculateDateTime();
      
      // Fetch updated content for all selected OUs
      await this.getContentUpdates();
      
      this.loading = false;
    } catch (error) {
      console.error('Error in onEndDateSelect:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update report data')
      });
      this.loading = false;
    }
  }

  async onEndDateClear() {
    try {
      this.loading = true;
      // Reset to today's date
      this.currentDate = new Date();
      
      // Recalculate the enhanced date
      this.calculateDateTime();
      
      // Fetch updated content for all selected OUs
      await this.getContentUpdates();
      
      this.loading = false;
    } catch (error) {
      console.error('Error in onEndDateClear:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update report data')
      });
      this.loading = false;
    }
  }

  async onStartDateSelect(event: Date) {
    try {
      this.loading = true;
      this.startDate = event;
      debugger
      const endDate = this.periodicReportForm.get('endDate')?.value;
      if (endDate && event && endDate < event) {
        this.periodicReportForm.get('endDate')?.setValue(event);
      }
      await this.getContentUpdates();
      this.loading = false;
    } catch (error) {
      console.error('Error in onStartDateSelect:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update report data')
      });
      this.loading = false;
    }
  }

  async onStartDateClear() {
    try {
      this.loading = true;
      this.startDate = null;
      await this.getContentUpdates();
      this.loading = false;
    } catch (error) {
      console.error('Error in onStartDateClear:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: this.translate('Failed to update report data')
      });
      this.loading = false;
    }
  }

  // Add a method to handle manager selection for each OU
  onManagerSelect(ouId: string, manager: any) {
    const ouData = this.selectedOUsData.find(data => data.ou._id === ouId);
    if (ouData) {
      ouData.selectedManager = manager;
    }
  }

  // Add a method to get manager by id for template use
  getManagerById(managers: any[], id: string) {
    return managers.find(m => m._id === id);
  }

  // Returns true if any of the section types has at least one data entry.
  // Used to collapse the "all sections empty" state into a single message
  // so that, when nothing happened in the selected period, we don't render
  // multiple empty section headers.
  hasAnyContentData(contentUpdates: any[]): boolean {
    if (!Array.isArray(contentUpdates) || contentUpdates.length === 0) {
      return false;
    }
    return contentUpdates.some((type: any) => Array.isArray(type?.data) && type.data.length > 0);
  }

}
